import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Tag, ChevronDown, ChevronRight, Package, Power } from "lucide-react";
import { authFetch } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import AdminLoading from "../../components/admin/AdminLoading";
import AdminEmptyState from "../../components/admin/AdminEmptyState";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import { toast } from "sonner";
import {
  ADMIN_FIELD,
  ADMIN_LABEL,
  ADMIN_PANEL,
  ADMIN_SELECT,
  BTN_OUTLINE,
  BTN_PRIMARY,
} from "../../config/brand";

const emptyOffer = {
  title: "",
  slug: "",
  short_description: "",
  description: "",
  image_url: "",
  benefits: "",
  promo_badge: "",
  promo_badge_color: "purple",
  category: "cursus",
  display_order: 0,
};

const emptyFormula = {
  name: "",
  description: "",
  price: 0,
  price_per_month: "",
  duration: "",
  duration_kind: "",
  benefits: "",
  badge: "",
  badge_color: "purple",
  display_order: 0,
};

const slugify = (str) =>
  str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export default function AdminOffers() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const [offerDialog, setOfferDialog] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [offerForm, setOfferForm] = useState(emptyOffer);
  const [offerErrors, setOfferErrors] = useState({});

  const [formulaDialog, setFormulaDialog] = useState(false);
  const [editingFormula, setEditingFormula] = useState(null);
  const [formulaOfferId, setFormulaOfferId] = useState(null);
  const [formulaForm, setFormulaForm] = useState(emptyFormula);
  const [formulaErrors, setFormulaErrors] = useState({});

  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadOffers = async () => {
    setLoading(true);
    try {
      const response = await authFetch("/api/admin/offers");
      if (response.ok) setOffers(await response.json());
      else toast.error("Impossible de charger les offres");
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOffers();
  }, []);

  const toggleExpand = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const openCreateOffer = () => {
    setEditingOffer(null);
    setOfferForm(emptyOffer);
    setOfferErrors({});
    setOfferDialog(true);
  };

  const openEditOffer = (offer) => {
    setEditingOffer(offer);
    setOfferForm({
      title: offer.title,
      slug: offer.slug,
      short_description: offer.short_description || "",
      description: offer.description || "",
      image_url: offer.image_url || "",
      benefits: (offer.benefits || []).join("\n"),
      promo_badge: offer.promo_badge || "",
      promo_badge_color: offer.promo_badge_color || "purple",
      category: offer.category || "cursus",
      display_order: offer.display_order ?? 0,
    });
    setOfferErrors({});
    setOfferDialog(true);
  };

  const validateOffer = () => {
    const next = {};
    if (!offerForm.title.trim()) next.title = "Le titre est requis";
    setOfferErrors(next);
    return Object.keys(next).length === 0;
  };

  const saveOffer = async () => {
    if (!validateOffer()) return;

    setSaving(true);
    const payload = {
      title: offerForm.title,
      slug: offerForm.slug || slugify(offerForm.title),
      short_description: offerForm.short_description,
      description: offerForm.description,
      image_url: offerForm.image_url,
      benefits: offerForm.benefits.split("\n").map((s) => s.trim()).filter(Boolean),
      promo_badge: offerForm.promo_badge || null,
      promo_badge_color: offerForm.promo_badge_color,
      category: offerForm.category,
      display_order: Number(offerForm.display_order) || 0,
    };

    try {
      const url = editingOffer ? `/api/admin/offers/${editingOffer.id}` : "/api/admin/offers";
      const method = editingOffer ? "PUT" : "POST";
      const response = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Erreur");
      }
      toast.success(editingOffer ? "Offre mise à jour" : "Offre créée");
      setOfferDialog(false);
      loadOffers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (offer) => {
    setTogglingId(offer.id);
    const nextActive = offer.active === false;
    try {
      const res = await authFetch(`/api/admin/offers/${offer.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active: nextActive }),
      });
      if (!res.ok) throw new Error("Erreur");
      toast.success(nextActive ? "Offre activée" : "Offre désactivée — les inscriptions existantes sont conservées");
      loadOffers();
    } catch {
      toast.error("Impossible de changer le statut");
    } finally {
      setTogglingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.type === "offer") {
        const response = await authFetch(`/api/admin/offers/${deleteTarget.id}`, { method: "DELETE" });
        if (!response.ok) throw new Error("Erreur");
        toast.success(`Offre « ${deleteTarget.name} » supprimée. Les inscriptions existantes sont conservées.`);
      } else {
        const response = await authFetch(
          `/api/admin/offers/${deleteTarget.offerId}/formulas/${deleteTarget.id}`,
          { method: "DELETE" }
        );
        if (!response.ok) throw new Error("Erreur");
        toast.success("Cursus supprimé");
      }
      setDeleteTarget(null);
      loadOffers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDeleting(false);
    }
  };

  const openCreateFormula = (offerId) => {
    setEditingFormula(null);
    setFormulaOfferId(offerId);
    setFormulaForm(emptyFormula);
    setFormulaErrors({});
    setFormulaDialog(true);
  };

  const openEditFormula = (offerId, formula) => {
    setEditingFormula(formula);
    setFormulaOfferId(offerId);
    setFormulaForm({
      name: formula.name,
      description: formula.description || "",
      price: formula.price,
      price_per_month: formula.price_per_month ?? "",
      duration: formula.duration || "",
      duration_kind: formula.duration_kind || "",
      benefits: (formula.benefits || []).join("\n"),
      badge: formula.badge || "",
      badge_color: formula.badge_color || "purple",
      display_order: formula.display_order ?? 0,
    });
    setFormulaErrors({});
    setFormulaDialog(true);
  };

  const validateFormula = () => {
    const next = {};
    if (!formulaForm.name.trim()) next.name = "Le nom est requis";
    if (formulaForm.price === "" || Number(formulaForm.price) < 0) {
      next.price = "Le prix doit être un nombre positif";
    }
    setFormulaErrors(next);
    return Object.keys(next).length === 0;
  };

  const saveFormula = async () => {
    if (!validateFormula()) return;

    setSaving(true);
    const payload = {
      name: formulaForm.name,
      description: formulaForm.description,
      price: Number(formulaForm.price) || 0,
      price_per_month:
        formulaForm.price_per_month === "" ? null : Number(formulaForm.price_per_month),
      duration: formulaForm.duration,
      duration_kind: formulaForm.duration_kind || null,
      benefits: formulaForm.benefits.split("\n").map((s) => s.trim()).filter(Boolean),
      badge: formulaForm.badge || null,
      badge_color: formulaForm.badge_color,
      display_order: Number(formulaForm.display_order) || 0,
    };

    try {
      const base = `/api/admin/offers/${formulaOfferId}/formulas`;
      const url = editingFormula ? `${base}/${editingFormula.id}` : base;
      const method = editingFormula ? "PUT" : "POST";
      const response = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Erreur");
      }
      toast.success(editingFormula ? "Cursus mis à jour" : "Cursus créé");
      setFormulaDialog(false);
      loadOffers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteDialogTitle = `Supprimer « ${deleteTarget?.name || ""} » ?`;
  const deleteDialogDescription =
    deleteTarget?.type === "formula"
      ? `Le cursus « ${deleteTarget?.name} » sera définitivement supprimé.`
      : `L'offre « ${deleteTarget?.name} » et ses cursus seront définitivement supprimés. Les inscriptions déjà enregistrées sont conservées.`;

  return (
    <div>
      <AdminPageHeader
        icon={Package}
        title="Offres & Cursus"
        description="Gérer les offres, leurs cursus et badges promo"
        action={
          <Button onClick={openCreateOffer} className={BTN_PRIMARY}>
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle offre
          </Button>
        }
      />

      {loading ? (
        <AdminLoading message="Chargement des offres..." />
      ) : offers.length === 0 ? (
        <AdminEmptyState
          icon={Package}
          title="Aucune offre"
          description="Créez votre première offre pour commencer à gérer les cursus."
          action={
            <Button onClick={openCreateOffer} className={BTN_PRIMARY}>
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle offre
            </Button>
          }
        />
      ) : (
        <div className="space-y-4">
          {offers.map((offer) => (
            <Card key={offer.id} className={`${ADMIN_PANEL} hover:border-slate-300 transition-colors`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <button
                    onClick={() => toggleExpand(offer.id)}
                    className="flex items-start gap-3 text-left flex-1 min-w-0"
                  >
                    {expanded[offer.id] ? (
                      <ChevronDown className="h-5 w-5 text-slate-400 mt-1 shrink-0" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-slate-400 mt-1 shrink-0" />
                    )}
                    {offer.image_url && (
                      <img
                        src={offer.image_url}
                        alt={offer.title}
                        className="w-14 h-14 rounded-lg object-cover shrink-0 border border-slate-200"
                      />
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-slate-900 font-semibold">{offer.title}</span>
                        {offer.promo_badge && (
                          <Badge className="bg-[rgba(58,0,224,0.08)] text-[#3a00e0]">
                            <Tag className="h-3 w-3 mr-1" />
                            {offer.promo_badge}
                          </Badge>
                        )}
                        {!offer.active && <Badge variant="secondary">Inactive</Badge>}
                      </div>
                      <p className="text-slate-400 text-sm truncate">
                        {offer.short_description || offer.description}
                      </p>
                      <p className="text-slate-500 text-xs mt-1">
                        {offer.formulas?.length || 0} cursus / formule(s)
                      </p>
                    </div>
                  </button>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => openEditOffer(offer)} aria-label="Modifier">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleActive(offer)}
                      disabled={togglingId === offer.id}
                      aria-label={offer.active === false ? "Activer" : "Désactiver"}
                      className={offer.active === false ? "text-emerald-600" : "text-amber-600"}
                    >
                      {togglingId === offer.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Power className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => setDeleteTarget({ type: "offer", id: offer.id, name: offer.title })}
                      aria-label={`Supprimer ${offer.title}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {expanded[offer.id] && (
                  <div className="mt-4 pl-8 border-l-2 border-slate-200 ml-2 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">Cursus / Formules</span>
                      <Button
                        variant="outline"
                        size="sm"
                        className={BTN_OUTLINE}
                        onClick={() => openCreateFormula(offer.id)}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Ajouter un cursus
                      </Button>
                    </div>
                    {(offer.formulas || []).length === 0 ? (
                      <p className="text-slate-500 text-sm py-2">Aucun cursus pour cette offre</p>
                    ) : (
                      offer.formulas.map((formula) => (
                        <div
                          key={formula.id}
                          className="flex items-center justify-between bg-slate-50 rounded-lg p-3 border border-slate-200 cursor-pointer hover:border-slate-300"
                          onClick={() => openEditFormula(offer.id, formula)}
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-slate-900 text-sm font-medium">{formula.name}</span>
                              {formula.badge && (
                                <Badge className="bg-[rgba(58,0,224,0.08)] text-[#3a00e0] text-xs">
                                  {formula.badge}
                                </Badge>
                              )}
                            </div>
                            <p className="text-slate-400 text-xs">
                              {formula.price} €
                              {formula.price_per_month ? ` / ${formula.price_per_month} € par mois` : ""}
                              {formula.duration ? ` — ${formula.duration}` : ""}
                            </p>
                          </div>
                          <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditFormula(offer.id, formula)}
                              aria-label="Modifier"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() =>
                                setDeleteTarget({
                                  type: "formula",
                                  id: formula.id,
                                  offerId: offer.id,
                                  name: formula.name,
                                })
                              }
                              aria-label="Supprimer"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Offer dialog */}
      <Dialog open={offerDialog} onOpenChange={setOfferDialog}>
        <DialogContent className={`${ADMIN_PANEL} text-slate-900 max-w-lg max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle>{editingOffer ? "Modifier l'offre" : "Nouvelle offre"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className={ADMIN_LABEL}>Titre *</Label>
              <Input
                value={offerForm.title}
                onChange={(e) => {
                  setOfferForm({
                    ...offerForm,
                    title: e.target.value,
                    slug: editingOffer ? offerForm.slug : slugify(e.target.value),
                  });
                  if (offerErrors.title) setOfferErrors({});
                }}
                className={`${ADMIN_FIELD} ${offerErrors.title ? "border-red-500" : ""}`}
              />
              {offerErrors.title && <p className="text-red-600 text-xs">{offerErrors.title}</p>}
            </div>
            <div className="space-y-2">
              <Label className={ADMIN_LABEL}>Slug (URL)</Label>
              <Input
                value={offerForm.slug}
                onChange={(e) => setOfferForm({ ...offerForm, slug: e.target.value })}
                className={ADMIN_FIELD}
              />
            </div>
            <div className="space-y-2">
              <Label className={ADMIN_LABEL}>Description courte</Label>
              <Input
                value={offerForm.short_description}
                onChange={(e) => setOfferForm({ ...offerForm, short_description: e.target.value })}
                className={ADMIN_FIELD}
              />
            </div>
            <div className="space-y-2">
              <Label className={ADMIN_LABEL}>Description</Label>
              <Textarea
                value={offerForm.description}
                onChange={(e) => setOfferForm({ ...offerForm, description: e.target.value })}
                className={ADMIN_FIELD}
              />
            </div>
            <div className="space-y-2">
              <Label className={ADMIN_LABEL}>Image principale (URL)</Label>
              <Input
                value={offerForm.image_url}
                onChange={(e) => setOfferForm({ ...offerForm, image_url: e.target.value })}
                placeholder="https://..."
                className={ADMIN_FIELD}
              />
            </div>
            <div className="space-y-2">
              <Label className={ADMIN_LABEL}>Points de l'offre (un par ligne)</Label>
              <Textarea
                value={offerForm.benefits}
                onChange={(e) => setOfferForm({ ...offerForm, benefits: e.target.value })}
                placeholder={"Matériel fourni sur place\nPetits groupes\nCertificat"}
                className={ADMIN_FIELD}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={ADMIN_LABEL}>Badge promo</Label>
                <Input
                  value={offerForm.promo_badge}
                  onChange={(e) => setOfferForm({ ...offerForm, promo_badge: e.target.value })}
                  placeholder="-20%, NOUVEAU..."
                  className={ADMIN_FIELD}
                />
              </div>
              <div className="space-y-2">
                <Label className={ADMIN_LABEL}>Couleur badge</Label>
                <select
                  value={offerForm.promo_badge_color}
                  onChange={(e) => setOfferForm({ ...offerForm, promo_badge_color: e.target.value })}
                  className={`${ADMIN_SELECT}`}
                >
                  <option value="purple">Violet</option>
                  <option value="orange">Orange</option>
                  <option value="green">Vert</option>
                  <option value="red">Rouge</option>
                  <option value="blue">Bleu</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={ADMIN_LABEL}>Catégorie</Label>
                <select
                  value={offerForm.category}
                  onChange={(e) => setOfferForm({ ...offerForm, category: e.target.value })}
                  className={`${ADMIN_SELECT}`}
                >
                  <option value="atelier">Atelier</option>
                  <option value="cursus">Cursus</option>
                  <option value="stage">Stage</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label className={ADMIN_LABEL}>Ordre d'affichage</Label>
                <Input
                  type="number"
                  value={offerForm.display_order}
                  onChange={(e) => setOfferForm({ ...offerForm, display_order: e.target.value })}
                  className={ADMIN_FIELD}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOfferDialog(false)} className={BTN_OUTLINE} disabled={saving}>
              Annuler
            </Button>
            <Button onClick={saveOffer} disabled={saving} className={BTN_PRIMARY}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Enregistrement...
                </>
              ) : (
                "Enregistrer"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Formula dialog */}
      <Dialog open={formulaDialog} onOpenChange={setFormulaDialog}>
        <DialogContent className={`${ADMIN_PANEL} text-slate-900 max-w-lg max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle>{editingFormula ? "Modifier le cursus" : "Nouveau cursus"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className={ADMIN_LABEL}>Nom *</Label>
              <Input
                value={formulaForm.name}
                onChange={(e) => {
                  setFormulaForm({ ...formulaForm, name: e.target.value });
                  if (formulaErrors.name) setFormulaErrors({});
                }}
                className={`${ADMIN_FIELD} ${formulaErrors.name ? "border-red-500" : ""}`}
              />
              {formulaErrors.name && <p className="text-red-600 text-xs">{formulaErrors.name}</p>}
            </div>
            <div className="space-y-2">
              <Label className={ADMIN_LABEL}>Description</Label>
              <Input
                value={formulaForm.description}
                onChange={(e) => setFormulaForm({ ...formulaForm, description: e.target.value })}
                className={ADMIN_FIELD}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={ADMIN_LABEL}>Prix (€) *</Label>
                <Input
                  type="number"
                  min={0}
                  value={formulaForm.price}
                  onChange={(e) => {
                    setFormulaForm({ ...formulaForm, price: e.target.value });
                    if (formulaErrors.price) setFormulaErrors({});
                  }}
                  className={`${ADMIN_FIELD} ${formulaErrors.price ? "border-red-500" : ""}`}
                />
                {formulaErrors.price && <p className="text-red-600 text-xs">{formulaErrors.price}</p>}
              </div>
              <div className="space-y-2">
                <Label className={ADMIN_LABEL}>Prix / mois (optionnel)</Label>
                <Input
                  type="number"
                  value={formulaForm.price_per_month}
                  onChange={(e) => setFormulaForm({ ...formulaForm, price_per_month: e.target.value })}
                  className={ADMIN_FIELD}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={ADMIN_LABEL}>Type de durée</Label>
                <select
                  value={formulaForm.duration_kind}
                  onChange={(e) => setFormulaForm({ ...formulaForm, duration_kind: e.target.value })}
                  className={ADMIN_SELECT}
                >
                  <option value="">Libre (cursus / atelier)</option>
                  <option value="half_day">Demi-journée</option>
                  <option value="full_day">Journée</option>
                  <option value="full_week">Semaine complète</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label className={ADMIN_LABEL}>Durée</Label>
                <Input
                  value={formulaForm.duration}
                  onChange={(e) => setFormulaForm({ ...formulaForm, duration: e.target.value })}
                  placeholder="10h, 20h, 30 séances…"
                  className={ADMIN_FIELD}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className={ADMIN_LABEL}>Points du cursus (un par ligne)</Label>
              <Textarea
                value={formulaForm.benefits}
                onChange={(e) => setFormulaForm({ ...formulaForm, benefits: e.target.value })}
                className={ADMIN_FIELD}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={ADMIN_LABEL}>Badge</Label>
                <Input
                  value={formulaForm.badge}
                  onChange={(e) => setFormulaForm({ ...formulaForm, badge: e.target.value })}
                  placeholder="POPULAIRE..."
                  className={ADMIN_FIELD}
                />
              </div>
              <div className="space-y-2">
                <Label className={ADMIN_LABEL}>Ordre</Label>
                <Input
                  type="number"
                  value={formulaForm.display_order}
                  onChange={(e) => setFormulaForm({ ...formulaForm, display_order: e.target.value })}
                  className={ADMIN_FIELD}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormulaDialog(false)} className={BTN_OUTLINE} disabled={saving}>
              Annuler
            </Button>
            <Button onClick={saveFormula} disabled={saving} className={BTN_PRIMARY}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Enregistrement...
                </>
              ) : (
                "Enregistrer"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}
        title={deleteDialogTitle}
        description={deleteDialogDescription}
        confirmLabel="Supprimer"
        onConfirm={confirmDelete}
        confirming={deleting}
      />
    </div>
  );
}
